import React, { useState } from "react";
import { FiAlertCircle, FiFile, FiFileText, FiMic, FiPlay } from "react-icons/fi";
import { Chip } from "./Chip";
import { FoodItemsList } from "./FoodItemsList";
import {
  downloadAttachment,
  useMediaUrl,
} from "../../hooks/comms/useMediaUrl";
import {
  ATTACHMENT_KINDS,
  formatDuration,
  formatFileSize,
} from "../../utils/commsAttachments";

/** Kinds shown as visual tiles; everything else renders as a row. */
const TILE_KINDS = [ATTACHMENT_KINDS.IMAGE, ATTACHMENT_KINDS.VIDEO];

/** Tiles rendered before the rest collapse behind a "+N" overlay. */
const MAX_VISIBLE_TILES = 4;

/** Longest edge of a single-attachment tile, in px. */
const LARGE_TILE_PX = 220;

/** Edge of a tile inside the multi-attachment grid, in px. */
const GRID_TILE_PX = 104;

const ROW_CLASS =
  "mt-2 flex w-full max-w-[260px] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs text-gray-700";

/**
 * Join the non-empty parts of a metadata line with a middot.
 * @param {Array<string|undefined|null>} parts
 * @returns {string}
 */
const metaLine = (parts) => parts.filter(Boolean).join(" · ");

/**
 * Unique purpose labels across a set of attachments, in first-seen order.
 * @param {import('../../utils/commsAttachments').ChatAttachment[]} attachments
 * @returns {string[]}
 */
const distinctPurposeLabels = (attachments) => [
  ...new Set(attachments.map((a) => a.purposeLabel).filter(Boolean)),
];

/**
 * Box a tile should occupy, preserving the media's aspect ratio when known so
 * the bubble doesn't shift once the bytes arrive.
 *
 * @param {import('../../utils/commsAttachments').ChatAttachment} attachment
 * @param {boolean} large
 * @returns {{ width: number, height: number }}
 */
const tileBox = (attachment, large) => {
  if (!large) return { width: GRID_TILE_PX, height: GRID_TILE_PX };
  const { width, height } = attachment;
  if (!width || !height) return { width: LARGE_TILE_PX, height: LARGE_TILE_PX };
  const scale = Math.min(LARGE_TILE_PX / width, LARGE_TILE_PX / height, 1);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

/**
 * Purpose badge (Food photo, Body photo, …). Renders nothing for GENERAL.
 * @param {{ attachment: import('../../utils/commsAttachments').ChatAttachment }} props
 */
const PurposeBadge = ({ attachment }) =>
  attachment.purposeLabel ? (
    <Chip tone="neutral" className="mt-1">
      {attachment.purposeLabel}
    </Chip>
  ) : null;

/**
 * Skeleton / failure state shown in place of a tile.
 * @param {{ box: { width: number, height: number }, error: Error|null }} props
 */
const TilePlaceholder = ({ box, error }) => (
  <span
    style={box}
    className={`flex items-center justify-center rounded-lg border border-gray-200 ${
      error ? "bg-gray-50" : "animate-pulse bg-gray-100"
    }`}
  >
    {error && (
      <span className="flex flex-col items-center gap-1 px-2 text-center text-[11px] text-gray-500">
        <FiAlertCircle aria-hidden="true" />
        <span>Unavailable</span>
      </span>
    )}
  </span>
);

/**
 * A photo tile. The signed URL is minted per blob_path and dropped straight
 * onto the img — media loads aren't CORS-gated.
 *
 * @param {Object} props
 * @param {import('../../utils/commsAttachments').ChatAttachment} props.attachment
 * @param {() => void} props.onOpen
 * @param {boolean} props.large
 * @param {boolean} props.eager
 * @param {number} props.overflowCount
 */
const ImageTile = ({ attachment, onOpen, large, eager, overflowCount }) => {
  const { url, error, refresh } = useMediaUrl(attachment);
  const box = tileBox(attachment, large);
  const alt = attachment.caption || attachment.fileName || "Photo attachment";

  if (!url) {
    return <TilePlaceholder box={box} error={error} />;
  }

  return (
    <TileButton label={alt} onOpen={onOpen} overflowCount={overflowCount}>
      <img
        src={url}
        alt={alt}
        width={box.width}
        height={box.height}
        loading={eager ? "eager" : "lazy"}
        onError={refresh}
        className={
          large ? "block max-h-[220px] max-w-[220px] object-contain" : "h-full w-full object-cover"
        }
      />
    </TileButton>
  );
};

/**
 * A video tile. `preload="metadata"` pulls only the header via a range request
 * — enough for a real first-frame poster — so a 100 MB clip costs almost
 * nothing until the coach opens it.
 *
 * @param {Object} props
 * @param {import('../../utils/commsAttachments').ChatAttachment} props.attachment
 * @param {() => void} props.onOpen
 * @param {boolean} props.large
 * @param {number} props.overflowCount
 */
const VideoTile = ({ attachment, onOpen, large, overflowCount }) => {
  const { url, error, refresh } = useMediaUrl(attachment);
  const box = tileBox(attachment, large);
  const label = attachment.caption || attachment.fileName || "Video attachment";
  const duration = formatDuration(attachment.durationMs);

  if (!url) {
    return <TilePlaceholder box={box} error={error} />;
  }

  return (
    <TileButton label={label} onOpen={onOpen} overflowCount={overflowCount}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        src={url}
        preload="metadata"
        muted
        playsInline
        tabIndex={-1}
        aria-hidden="true"
        onError={refresh}
        style={box}
        className="pointer-events-none bg-gray-800 object-cover"
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white">
          <FiPlay aria-hidden="true" />
        </span>
      </span>
      {duration && (
        <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {duration}
        </span>
      )}
    </TileButton>
  );
};

/**
 * Shared clickable tile frame with the optional "+N" scrim.
 *
 * @param {Object} props
 * @param {string} props.label - Accessible name for the open action.
 * @param {() => void} props.onOpen
 * @param {number} props.overflowCount
 * @param {React.ReactNode} props.children
 */
const TileButton = ({ label, onOpen, overflowCount, children }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={`Open ${label}`}
    className="relative block overflow-hidden rounded-lg border border-gray-200 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-primary/50"
  >
    {children}
    {overflowCount > 0 && (
      <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-semibold text-white">
        +{overflowCount}
      </span>
    )}
  </button>
);

/**
 * A voice note, played inline. `preload="none"` means no audio is fetched
 * until the coach presses play.
 *
 * @param {{ attachment: import('../../utils/commsAttachments').ChatAttachment }} props
 */
const VoiceRow = ({ attachment }) => {
  const { url, error, refresh } = useMediaUrl(attachment);
  const duration = formatDuration(attachment.durationMs);
  const label = attachment.caption || attachment.fileName || "Voice note";

  return (
    <div className={ROW_CLASS}>
      <FiMic aria-hidden="true" className="flex-shrink-0 text-gray-500" />
      {url ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          src={url}
          controls
          preload="none"
          onError={refresh}
          aria-label={label}
          className="h-8 max-w-[200px]"
        />
      ) : (
        <span className="text-[11px] text-gray-500">
          {error ? error.message : "Loading…"}
        </span>
      )}
      {duration && !error && (
        <span className="flex-shrink-0 text-[11px] text-gray-500">{duration}</span>
      )}
    </div>
  );
};

/**
 * A PDF chip that opens the viewer, or a download chip for any other file.
 * Neither fetches until the coach acts on it.
 *
 * @param {Object} props
 * @param {import('../../utils/commsAttachments').ChatAttachment} props.attachment
 * @param {() => void} props.onOpen
 */
const FileRow = ({ attachment, onOpen }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isPdf = attachment.kind === ATTACHMENT_KINDS.PDF;
  const Icon = isPdf ? FiFileText : FiFile;
  const label = attachment.fileName || (isPdf ? "PDF" : "Attachment");
  const meta = metaLine([
    attachment.pageCount
      ? `${attachment.pageCount} page${attachment.pageCount === 1 ? "" : "s"}`
      : "",
    formatFileSize(attachment.fileSize),
    error ? error.message : "",
  ]);

  const handleDownload = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadAttachment(attachment);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={isPdf ? onOpen : handleDownload}
      disabled={busy}
      aria-label={isPdf ? `Open ${label}` : `Download ${label}`}
      className={`${ROW_CLASS} hover:bg-gray-100 disabled:opacity-60`}
    >
      <Icon aria-hidden="true" className="flex-shrink-0 text-gray-500" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{label}</span>
        {(meta || busy) && (
          <span className={`block text-[11px] ${error ? "text-danger" : "text-gray-500"}`}>
            {busy ? "Downloading…" : meta}
          </span>
        )}
      </span>
    </button>
  );
};

/**
 * Renders every attachment on a message inside a chat bubble: photo and video
 * tiles, voice players, PDF and file chips, plus captions, purpose badges and
 * any food items the client tagged.
 *
 * Presentational only — the parent owns the lightbox and receives open
 * requests via onOpen.
 *
 * @param {Object} props
 * @param {import('../../utils/commsAttachments').ChatAttachment[]} props.attachments
 * @param {(index: number) => void} props.onOpen - Open the viewer at an index.
 */
export const AttachmentGrid = ({ attachments, onOpen }) => {
  if (!attachments || !attachments.length) return null;

  const tiles = attachments.filter((a) => TILE_KINDS.includes(a.kind));
  const rows = attachments.filter((a) => !TILE_KINDS.includes(a.kind));
  const isSingleTile = tiles.length === 1 && !rows.length;
  const visibleTiles = tiles.slice(0, MAX_VISIBLE_TILES);
  const hiddenCount = tiles.length - visibleTiles.length;
  const foodBearing = attachments.filter((a) => a.foodItems.length > 0);

  return (
    <div className="mt-2">
      {visibleTiles.length > 0 && (
        <div className={isSingleTile ? "" : "flex max-w-[220px] flex-wrap gap-1"}>
          {visibleTiles.map((attachment, i) => {
            const shared = {
              attachment,
              large: isSingleTile,
              overflowCount: i === visibleTiles.length - 1 ? hiddenCount : 0,
              onOpen: () => onOpen(attachment.index),
            };
            return attachment.kind === ATTACHMENT_KINDS.VIDEO ? (
              <VideoTile key={attachment.id} {...shared} />
            ) : (
              <ImageTile key={attachment.id} {...shared} eager={i === 0} />
            );
          })}
        </div>
      )}

      {rows.map((attachment) =>
        attachment.kind === ATTACHMENT_KINDS.VOICE ? (
          <VoiceRow key={attachment.id} attachment={attachment} />
        ) : (
          <FileRow
            key={attachment.id}
            attachment={attachment}
            onOpen={() => onOpen(attachment.index)}
          />
        )
      )}

      {/* A caption only reads unambiguously next to a single attachment; with
          several, the lightbox shows each one against its own media. */}
      {attachments.length === 1 ? (
        <div className="mt-1">
          {attachments[0].caption && (
            <p className="break-words text-xs text-gray-600">
              {attachments[0].caption}
            </p>
          )}
          <PurposeBadge attachment={attachments[0]} />
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1">
          {distinctPurposeLabels(attachments).map((label) => (
            <Chip key={label} tone="neutral">
              {label}
            </Chip>
          ))}
        </div>
      )}

      {foodBearing.map((attachment) => (
        <FoodItemsList key={`food-${attachment.id}`} items={attachment.foodItems} />
      ))}
    </div>
  );
};

export default AttachmentGrid;
